import React, { useState } from 'react';
import { Phone, Send, Calendar, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { contactData } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    serviceType: '',
    location: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        eventDate: '',
        guestCount: '',
        serviceType: '',
        location: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <section id="contact" className="py-24 bg-stone-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23d97706' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gold-500/10 border border-gold-500/20 rounded-full px-4 py-2 mb-6">
            <Calendar className="w-4 h-4 text-gold-400" />
            <span className="text-gold-300 text-sm font-medium">Book Your Event</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Let's Make <span className="text-gold-400">Music Together</span>
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Ready to bring authentic country music to your event? Get in touch and let's discuss how we can make your occasion unforgettable.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-stone-950/50 border border-stone-800 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Get In Touch</h3>
              
              <div className="space-y-4">
                <a
                  href={`tel:${contactData.receptionist.phone}`}
                  className="flex items-center gap-4 p-4 bg-stone-900/50 border border-stone-800 rounded-xl hover:border-gold-500/30 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center group-hover:bg-gold-500/30 transition-colors">
                    <Phone className="w-5 h-5 text-gold-400" />
                  </div>
                  <div>
                    <p className="text-stone-400 text-sm">{contactData.receptionist.name}</p>
                    <p className="text-white font-semibold">{contactData.receptionist.phone}</p>
                  </div>
                </a>

                <a
                  href={`tel:${contactData.human.phone}`}
                  className="flex items-center gap-4 p-4 bg-stone-900/50 border border-stone-800 rounded-xl hover:border-gold-500/30 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <Phone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-stone-400 text-sm">{contactData.human.name}</p>
                    <p className="text-white font-semibold">{contactData.human.phone}</p>
                  </div>
                </a>
              </div>

              <div className="mt-6 pt-6 border-t border-stone-800">
                <p className="text-stone-400 text-sm mb-2">Prefer to text?</p>
                <p className="text-stone-300">Call or text either number to discuss your event!</p>
              </div>
            </div>

            {/* ASCAP Info */}
            <div className="bg-stone-950/50 border border-stone-800 rounded-2xl p-6">
              <p className="text-gold-400 text-sm font-medium mb-2">Licensed Presenter through ASCAP</p>
              <p className="text-stone-400 text-sm">License #: {contactData.ascap.licenseNumber}</p>
              <p className="text-stone-500 text-xs mt-2">
                Questions? Contact {contactData.ascap.representative} at {contactData.ascap.phone}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-stone-950/50 border border-stone-800 rounded-2xl p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-stone-400">We'll get back to you soon to discuss your event.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Your Name *</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Phone</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Event Date</label>
                      <Input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleChange}
                        className="bg-stone-900 border-stone-700 text-white focus:border-gold-500"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Event Type</label>
                      <Select onValueChange={(value) => handleSelectChange('eventType', value)}>
                        <SelectTrigger className="bg-stone-900 border-stone-700 text-white">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700">
                          <SelectItem value="wedding">Wedding</SelectItem>
                          <SelectItem value="corporate">Corporate Event</SelectItem>
                          <SelectItem value="private">Private Party</SelectItem>
                          <SelectItem value="bar">Bar/Pub</SelectItem>
                          <SelectItem value="festival">Festival/Fair</SelectItem>
                          <SelectItem value="fundraiser">Fundraiser</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Service Type</label>
                      <Select onValueChange={(value) => handleSelectChange('serviceType', value)}>
                        <SelectTrigger className="bg-stone-900 border-stone-700 text-white">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700">
                          <SelectItem value="solo">Solo Acoustic</SelectItem>
                          <SelectItem value="duo">Duo</SelectItem>
                          <SelectItem value="fullband">Full Band</SelectItem>
                          <SelectItem value="largeband">Large Band (4+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-stone-300 text-sm mb-2">Est. Guests</label>
                      <Input
                        type="number"
                        name="guestCount"
                        value={formData.guestCount}
                        onChange={handleChange}
                        placeholder="100"
                        className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-300 text-sm mb-2">Event Location</label>
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City, State"
                      className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-300 text-sm mb-2">Tell Us About Your Event</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Share any details about your event, special requests, or questions..."
                      className="bg-stone-900 border-stone-700 text-white placeholder:text-stone-500 focus:border-gold-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gold-500 hover:bg-gold-600 text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/20"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Booking Request
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
